import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import type { Mentor, SkillBookItem } from '@mezha/api'
import { useMentorQuery, useMentorsQuery, useTrainMutation } from '../../api/endpoints/mentors'
import { useEnterMutation, useLeaveMutation, useWorldQuery } from '../../api/endpoints/world'
import { useCommand } from '../../lib/useCommand'
import { useShellMode } from '../../shell/ShellContext'
import { ConfirmInPlace, PlankButton } from '../../ui'
import { Emblem } from '../../ui/brand/Emblems'
import { Failure, Loading } from '../../ui/State'
import { LocationWindow } from '../LocationWindow'
import { silverText } from '../common'
import { KIND_EMBLEM } from '../skills/words'
import './mentors.css'

function learningBlock(m: Mentor, k: SkillBookItem, price: number): string {
  if (m.address.block) return m.address.block
  if (!m.address.here) return `Для обучения придите: ${m.address.place_name}.`
  if (m.advanced_block) return m.advanced_block
  if (k.next?.block === 'skill_locked') {
    const tier = m.school.tiers.find(t => t.tier === k.tier)
    if (!tier) return 'Сначала откройте ступень этого умения в школе.'
    const title = `${['I', 'II', 'III', 'IV', 'V'][tier.tier - 1] ?? tier.tier} ступень «${tier.title}»`
    const lesson = !m.school.tiers[0]?.open ? ' Сначала завершите первый урок у наставника.' : ''
    const previous = m.school.tiers.find(t => t.tier < k.tier && !t.open && t.gate === 'trial')
    const prerequisite = previous ? ` Сначала пройдите предыдущее испытание «${previous.title}».` : ''
    const trial = tier.gate === 'trial' ? ' Затем пройдите испытание этой ступени у наставника.' : ''
    return `Закрыта ${title}. Нужны уровень героя ${tier.level} (сейчас ${m.level}) и мастерство ${tier.mastery} (сейчас ${m.school.mastery.level}).${lesson}${prerequisite}${trial}`
  }
  if (k.next?.block === 'mastery_required') return `Нужно мастерство ${k.next.need_mastery}, сейчас ${m.school.mastery.level}.`
  if (k.next?.block === 'not_enough_skill_points') return `Нужно 1 свободное очко умений, сейчас ${m.points.free}.`
  if (k.next?.block) return 'Обучение пока недоступно. Проверьте условия ступени в книге умений.'
  return Number(m.silver) < price ? `Нужно ${silverText(price)}, сейчас ${silverText(Number(m.silver))}.` : ''
}

export function MentorLink({ school }: { school: string }) {
  const query = useMentorsQuery()
  const m = query.data?.find(x => x.school_id === school)
  return <div className="mentor-link">{m ? <><Link to={`/play/mentor/${m.id}`}>Наставник: {m.name} →</Link><span>{m.place_name}{m.block ? ` · ${m.block}` : ''}</span></> : query.isError ? <button onClick={() => void query.refetch()}>Не удалось найти наставника — повторить</button> : query.data ? 'Наставник этой школы пока не появился в мире.' : 'Ищем наставника…'}</div>
}

/** Enter the actual place through world commands; never teleport from the book. */
export function VisitPlace({ place, name, x, y }: { place: string; name: string; x: number; y: number }) {
  const world = useWorldQuery()
  const [enter] = useEnterMutation()
  const [leave] = useLeaveMutation()
  const cmd = useCommand(() => void world.refetch())
  const w = world.data
  const same = w?.x === x && w.y === y
  const visit = async () => {
    if (!w) return
    const current = w.place_id ? await cmd.run(() => leave({ expected_version: w.version }).unwrap()) : w
    if (current) await cmd.run(() => enter({ expected_version: current.version, place_id: place }).unwrap())
  }
  const params = new URLSearchParams({ destination: name, place, x: String(x), y: String(y) })
  return <div className="mentor-visit">
    <p>{name} · клетка {x}, {y}</p>
    {same ? <PlankButton onClick={() => void visit()}>Войти: {name}</PlankButton> : <Link className="plank" to={`/play/map?${params}`}>Показать путь на карте</Link>}
    {cmd.text && <p role="alert">{cmd.text}</p>}
  </div>
}

export function MentorScreen() {
  useShellMode('window')
  const { id = '' } = useParams()
  const query = useMentorQuery(id, { refetchOnMountOrArgChange: true })
  const [train] = useTrainMutation()
  const navigate = useNavigate()
  const cmd = useCommand(() => void query.refetch(), () => void query.refetch())
  const [done, setDone] = useState('')
  const m = query.data
  const run = async (op: 'lesson' | 'trial' | 'learn' | 'reset', skill_id?: string, tier?: number) => {
    if (!m) return
    setDone('')
    const result = await cmd.run(() => train({ id, op, expected_version: m.version, skill_id, tier }).unwrap())
    if (!result) return
    if ('battle_id' in result) navigate(`/play/battle/${result.battle_id}`, { state: { from: `/play/mentor/${id}` } })
    else {
      const learned = result.school.skills.find(k => k.id === skill_id)
      setDone(op === 'reset' ? 'Ранги сброшены, очки возвращены. Мастерство сохранено.' : `Ранг изучен. ${learned?.in_set ? 'Приём в боевом наборе.' : learned?.kind === 'passive' ? 'Пассивное умение действует при выполнении его условий.' : 'Выберите приём в книге умений, чтобы добавить его в боевой набор.'}`)
    }
  }
  return <LocationWindow title={m ? `${m.address.name} · ${m.school.name}` : 'Наставник'} emblem="nav.skills" idBase="mentor" bodyClassName="mentor-body">
    {query.isLoading && <Loading />}
    {query.isError && !m && <Failure error={query.error} retry={() => void query.refetch()} what="Наставник" />}
    {m && <>
      <div className="mentor-intro parch">
        {m.address.portrait && <img src={m.address.portrait} alt="" />}
        <div><h3>{m.address.name}</h3><p>{m.greeting}</p><p>{m.address.place_name} · мастерство {m.school.mastery.level} · свободных очков: {m.points.free} · {silverText(Number(m.silver))}</p></div>
      </div>
      {m.address.block && <p className="mentor-requirement">{m.address.block}</p>}
      {!m.address.here && <VisitPlace place={m.address.place_id} name={m.address.place_name} x={m.address.x} y={m.address.y} />}
      {cmd.text && <p role="alert" className="mentor-requirement">{cmd.text}</p>}
      {done && <p role="status">{done}</p>}
      <MentorActions m={m} run={run} />
      <Link to={`/play/skills?school=${m.school.id}`}>К книге умений и боевому набору →</Link>
    </>}
  </LocationWindow>
}

function MentorActions({ m, run }: { m: Mentor; run: (op: 'lesson' | 'trial' | 'learn' | 'reset', skill?: string, tier?: number) => Promise<void> }) {
  const available = m.address.here && !m.address.block
  const learned = m.school.skills.some(k => k.rank > 0)
  return <>
    {!m.school.tiers[0]?.open && <section className="mentor-panel">
      <h3>Первый урок · I ступень</h3>
      <p>{m.lesson_needs_win ? 'Нужно победить наставника за три раунда.' : 'Завершите три раунда или победите наставника.'} Сдача не засчитывается. Здоровье и мана сохраняются, опыта за урок нет. Повторные попытки бесплатны.</p>
      <ConfirmInPlace question={`Начать урок за ${silverText(m.lesson_price)}?`} onConfirm={() => run('lesson')}>
        {ask => <PlankButton gold disabled={!available || Number(m.silver) < m.lesson_price} onClick={ask}>Первый урок · {m.lesson_price ? silverText(m.lesson_price) : 'бесплатно'}</PlankButton>}
      </ConfirmInPlace>
    </section>}
    {m.advanced_block && <p className="mentor-requirement">{m.advanced_block}</p>}
    <div className="mentor-skills">{m.school.skills.map(k => {
      const price = m.rank_prices[k.tier - 1]
      const why = k.next ? learningBlock(m, k, price) : ''
      const reasonID = `mentor-lock-${k.id}`
      return <section key={k.id} className={`mentor-panel${why ? ' mentor-panel--locked' : ''}`}>
        <h3 className="mentor-skill-title"><Emblem name={KIND_EMBLEM[k.kind]} size={36} /><span>{k.name} <small>ранг {k.rank}/{k.max_rank}</small></span></h3>
        {why && <div className="mentor-lock"><Emblem name="system.lock" size={20} /><strong>Изучение недоступно</strong></div>}
        <p>{k.description}</p>
        {k.next && <p>Следующий ранг: {k.ranks[k.next.rank - 1]}<br />Нужно мастерство {k.next.need_mastery}, 1 очко и {silverText(price)}.</p>}
        {why && <p id={reasonID} className="mentor-lock-reason">{why}</p>}
        {!k.next && <p className="note">Последний ранг изучен</p>}
        {k.next && <ConfirmInPlace key={`${k.id}-${k.rank}-${price}`} question={`Изучить «${k.name}», ранг ${k.next.rank}, за 1 очко и ${silverText(price)}?`} onConfirm={() => run('learn', k.id)}>
          {ask => <PlankButton locked={!!why} aria-describedby={why ? reasonID : undefined} onClick={ask}>{why ? <><Emblem name="system.lock" size={18} />Недоступно · ранг {k.next?.rank}</> : <>Изучить ранг {k.next?.rank} · {silverText(price)}</>}</PlankButton>}
        </ConfirmInPlace>}
      </section>
    })}</div>
    <div className="mentor-skills">{m.school.tiers.filter(t => t.gate === 'trial').map(t => <section key={t.tier} className="mentor-panel">
      <h3>Испытание · {t.title}</h3><p>{t.open ? 'Испытание пройдено.' : `Победите в учебном поединке. Нужны уровень ${t.level}, мастерство ${t.mastery} и предыдущая ступень. Награда: ступень и 1 очко умений.`}</p>
      {!t.open && <ConfirmInPlace question="Начать испытание? Здоровье и мана сохранятся." onConfirm={() => run('trial', undefined, t.tier)}>
        {ask => <PlankButton disabled={!available || !!m.advanced_block || m.level < t.level || m.school.mastery.level < t.mastery || !m.school.tiers[t.tier - 2]?.open} onClick={ask}>Пройти испытание</PlankButton>}
      </ConfirmInPlace>}
    </section>)}</div>
    {learned && <section className="mentor-panel"><h3>Переучиться в этой школе</h3><p>Все ранги школы будут сняты с боевого набора, очки вернутся. Мастерство и открытые ступени сохранятся. Первый сброс среди всех школ бесплатный; следующие — 20 серебра за уровень героя.</p>
      <ConfirmInPlace key={m.reset_price} question={`Сбросить все ранги школы «${m.school.name}» за ${silverText(m.reset_price)}? Потраченное на обучение серебро не возвращается.`} onConfirm={() => run('reset')}>
        {ask => <PlankButton disabled={!available || Number(m.silver) < m.reset_price} onClick={ask}>Сбросить ранги · {m.reset_price ? silverText(m.reset_price) : 'бесплатно'}</PlankButton>}
      </ConfirmInPlace>
    </section>}
  </>
}
